import React, { createContext, useContext, useEffect, useState } from "react";
import { rtdb } from "utils/firebase";
import { environment } from "utils/constant";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children, organizationId, userId, isOwner }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);
  
  // Listen for real-time notifications
  useEffect(() => {
    if (!organizationId || !isOwner) return;

    const notificationsRef = rtdb.ref(`notifications-${environment}`);

    const handleNotificationChange = (snapshot) => {
      const notificationsData = snapshot.val() || {};
      const notificationsList = Object.entries(notificationsData)
        .map(([key, value]) => ({ id: key, ...value }))
        .filter((notification) => notification.organization === organizationId)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setNotifications(notificationsList);

      const unread = notificationsList.filter((n) => !n.read).length;
      setUnreadCount(unread);

      // Update browser tab title
      updateTabTitle(unread);
    };

    // Listen for new notifications
    const handleChildAdded = (snapshot) => {
      const notification = snapshot.val();
      if (notification && notification.organization === organizationId) {
        // Check if this is a genuinely new notification (within last 30 seconds)
        const notificationTime = new Date(notification.date);
        const now = new Date();
        const timeDiff = (now - notificationTime) / 1000; // in seconds
        const actor_id = notification.actor_id || null;
        console.log("New notification received:", notification,"UserId:", userId, "ActorId:", actor_id);
        if (!notification.read && actor_id !== userId) {
          console.log("Setting new notification for popup:", notification);
          setNewNotification({ id: snapshot.key, ...notification });

          // Show browser notification if permission granted
          showBrowserNotification(notification);

          // Play notification sound
          playNotificationSound();
        }
      }
    };

    notificationsRef
      .orderByChild("organization")
      .equalTo(organizationId)
      .on("value", handleNotificationChange);

    notificationsRef
      .orderByChild("organization")
      .equalTo(organizationId)
      .on("child_added", handleChildAdded);

    return () => {
      notificationsRef.off("value", handleNotificationChange);
      notificationsRef.off("child_added", handleChildAdded);
    };
  }, [organizationId]);

  const updateTabTitle = (count) => {
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;

    // Update favicon with badge
    // updateFavicon(count);
  };

  const updateFavicon = (count) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = 32;
    canvas.height = 32;

    img.onload = () => {
      // Draw the original favicon
      ctx.drawImage(img, 0, 0, 32, 32);

      if (count > 0) {
        // Draw notification badge with larger size
        ctx.fillStyle = "#FF0000"; // Brighter red
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, 2 * Math.PI); // Increased radius from 8 to 10
        ctx.fill();

        // Add a white border for better visibility
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw count text with improved visibility
        ctx.fillStyle = "white";
        ctx.font = "bold 11px Arial"; // Increased font size
        ctx.textAlign = "center";
        ctx.textBaseline = "middle"; // Better text alignment
        ctx.fillText(count > 99 ? "99+" : count.toString(), 24, 8);
      }

      // Update favicon
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL();
    };

    // Load current favicon or use default
    img.src =
      document.querySelector("link[rel*='icon']")?.href || "/download.svg";
  };

  useEffect(() => {
    if (!isOwner) return;
    const unread = notifications.filter((n) => !n.read).length;
    setUnreadCount(unread);
    updateTabTitle(unread);
  }, [notifications]);

  const showBrowserNotification = (notification) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.message,
        icon: "/download.svg",
        tag: notification.id, // Prevents duplicate notifications
      });
    }
  };

  const playNotificationSound = () => {
    // You can add a notification sound file to your public folder
    const audio = new Audio("/web-notification.wav");
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignore errors if sound can't play
    });
  };

  const markAsRead = (notificationId) => {
    const notificationsRef = rtdb.ref(`notifications-${environment}`);
    notificationsRef.child(notificationId).update({ read: true });
  };

  const markAllAsRead = () => {
    const notificationsRef = rtdb.ref(`notifications-${environment}`);
    notifications
      .filter((n) => !n.read)
      .forEach((notification) => {
        notificationsRef.child(notification.id).update({ read: true });
      });
  };

  const dismissNewNotification = () => {
    setNewNotification(null);
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return Notification.permission === "granted";
  };

  const value = {
    notifications,
    unreadCount,
    newNotification,
    markAsRead,
    markAllAsRead,
    dismissNewNotification,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
